import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useId, useEffect } from 'react';
import { useCombinedRefs, twMerge } from '@belong/common';
import clsx from 'clsx';
import { Form } from '../Form/Form.js';
import css from './TextInput.module.css.js';

const TextInput = /*#__PURE__*/forwardRef(function TextInput({
  id,
  value = '',
  name,
  label,
  error,
  focused,
  leftElement,
  RightElement,
  variant = 'primary',
  mask,
  unmask,
  className,
  errorMessageVisible,
  autoEllipsis = false,
  ...rest
}, ref) {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [cursors, setCursors] = useState({});
  const innerRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, innerRef);
  const isFocused = focused || isInputFocused;
  const _id = id || useId();
  const labelId = `label-${_id}`;
  let _value = value;
  function onFocus(event) {
    if (rest.onFocus) {
      rest.onFocus(event);
    }
    setIsInputFocused(true);
  }
  function onBlur(event) {
    if (rest.onBlur) {
      rest.onBlur(event);
    }
    setIsInputFocused(false);
  }
  const shouldHandleMasking = !!mask && !!unmask;
  /*
    Since masking changes the length of the input value as the user types, the way the input handles is the cursor is to always
    send it to the end of the input, which feels very weird when adding or deleting values from the middle of the input. This method
    followed by the useEffect fixes that.
   */
  function handleMasking(event) {
    if (shouldHandleMasking) {
      const previousValue = String(_value || '');
      const previousUnmaskedValue = unmask(previousValue) || '';
      const previousValueDiff = previousValue.length - previousUnmaskedValue.length;
      const unmaskedValue = unmask(event.target.value);
      let maskedValue;
      try {
        maskedValue = mask(unmaskedValue);
      } catch (e) {
        setCursors({
          cursor: Number(event.target.selectionStart) - 1,
          calculatedCursor: Number(event.target.selectionStart) - 1
        });
        event.target.value = previousUnmaskedValue;
        return;
      }
      const currentValueDiff = maskedValue.length - unmaskedValue.length;
      const calculatedSelectionStart = Number(event.target.selectionStart) + (currentValueDiff - previousValueDiff);
      setCursors({
        cursor: Number(event.target.selectionStart),
        calculatedCursor: calculatedSelectionStart
      });
      event.target.value = unmask(event.target.value);
    }
  }
  function handleChange(event) {
    if (shouldHandleMasking) {
      // This mutates the event object value.
      handleMasking(event);
    }
    if (rest.onChange) {
      rest.onChange(event);
    }
  }
  function handleKeyDown(event) {
    if (shouldHandleMasking && innerRef?.current && event.key === 'Backspace') {
      const cursorPosition = innerRef?.current.selectionStart || 0;
      const isEditingInMiddle = cursorPosition > 0 && cursorPosition < innerRef.current.value.length;
      if (isEditingInMiddle) {
        innerRef.current.value = '';
      }
    }
    if (rest.onKeyDown) {
      rest.onKeyDown(event);
    }
  }
  useEffect(() => {
    const input = combinedRef.current;
    // Only mutate cursor when the current element is the active element, since on safari, mutating the cursor
    // counts as touching the input and we trigger validation errors.
    if (input && document.activeElement === input) {
      const cursorValue = cursors.cursor || 0;
      const calculatedCursorValue = cursors.calculatedCursor || 0;
      if (calculatedCursorValue < cursorValue) {
        input.selectionStart = cursorValue;
        input.selectionEnd = cursorValue;
      } else {
        input.selectionStart = calculatedCursorValue;
        input.selectionEnd = calculatedCursorValue;
      }
    }
  }, [cursors]);
  if (shouldHandleMasking) {
    try {
      _value = mask(String(value));
    } catch (e) {}
  }
  return jsxs("div", {
    className: clsx(error && errorMessageVisible ? 'flex w-full flex-col' : 'contents'),
    children: [jsxs("div", {
      className: twMerge(clsx('relative flex w-full items-end focus-within:border-green', {
        [`border-0 border-b-2 border-solid border-gray ${css.container}`]: ['primary', 'secondary'].includes(variant)
      }, {
        'border-green': focused || variant === 'secondary' && value
      }, {
        'border-red': !!error
      })),
      children: [leftElement && jsx("div", {
        className: "absolute bottom-sm left-0",
        children: leftElement
      }), jsx("input", {
        className: twMerge('w-full outline-none', css.textInput, variantToInputClassNameMap[variant], rest.disabled && 'text-gray', clsx({
          'border-green': isFocused && variant === 'filled'
        }), autoEllipsis ? isFocused ? 'whitespace-normal overflow-auto text-clip' : 'whitespace-nowrap overflow-hidden text-ellipsis' : '', className),
        ref: combinedRef,
        id: _id || name,
        name: name,
        value: _value,
        ...rest,
        onFocus: onFocus,
        onBlur: onBlur,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
        placeholder: variant === 'filled' ? label : rest.placeholder
      }), RightElement && jsx("div", {
        className: clsx('absolute right-0', css.rightElement),
        children: RightElement
      }), variant !== 'filled' && jsx("label", {
        id: labelId,
        className: twMerge(clsx('pointer-events-none absolute left-0', variantToLabelClassNameMap[variant], {
          [clsx(css.lifted, variantToLiftedLabelClassNameMap[variant])]: value !== ''
        }, {
          'text-red': !!error
        }, {
          [clsx(css.focused, 'text-green')]: isFocused
        }, rest.disabled && 'text-gray', css.inputLabel)),
        htmlFor: _id || name,
        children: label
      })]
    }), errorMessageVisible && error && jsx(Form.ErrorMessage, {
      message: error
    })]
  });
});
const variantToInputClassNameMap = {
  primary: 'pb-sm',
  secondary: 'pb-sm text-white',
  filled: `border-solid border border-white py-sm px-xs rounded-small text-light-gray bg-darker-navy/50`
};
const variantToLabelClassNameMap = {
  primary: 'text-dark-gray',
  secondary: 'text-white',
  filled: ''
};
const variantToLiftedLabelClassNameMap = {
  primary: 'text-navy',
  secondary: 'text-green',
  filled: ''
};

export { TextInput };
